import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0Select } from '@factorialco/f0-react'
import { Lightbulb } from '@factorialco/f0-react/icons/app'
import { Pin } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  GenerateAutoScheduleDocument,
  ShiftManagementAutoScheduleStatusDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters, {
  applyFilters,
} from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import { Groups } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import employees from 'modules/employees/store/collections/employees'
import getFilterValue from 'modules/shift_management/components/ShiftManagement/Content/_filters/getFilterValue'
import EmployeesSelector from 'modules/shift_management/components/ShiftManagement/EmployeesSelector'
import useLocationIdsFilter from 'modules/shift_management/hooks/useLocationIdsFIlter'
import { useOnSelectEmployees } from 'modules/shift_management/hooks/useOnSelectEmployees'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { AutoScheduleForm } from 'modules/shift_management/lib/forms/AutoScheduleForm'
import { useSelectableLocations } from 'modules/shift_management/lib/useSelectableLocations'

import { Footer } from './Footer'
import { Rules } from './Rules'

interface Props {
  onClose: () => void
  setIsSubscribed: (isSubscribed: boolean) => void
}

const NewAutoScheduleModal = observer(({ onClose, setIsSubscribed }: Props) => {
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([])
  const { employee, allMyDescendants } = useCurrentSession()
  const employeeId = employee?.id

  const apolloClient = useApolloClient()

  const [{ view }] = useSchedule()

  const form = useLazyRef(
    () =>
      new AutoScheduleForm({
        start_at: view.startAt,
        end_at: view.endAt,
      })
  ).current

  const [{ canCreateShiftsForEverybody }] = usePermissions()
  const { loading: locationsLoading, locations } = useSelectableLocations(
    employeeId,
    canCreateShiftsForEverybody
  )
  const locationIds = useLocationIdsFilter()
  const currentLocationId = useUnsafeMemo(() => {
    return Number(form.get('location_id').value) || Number(locationIds.at(0))
  }, [locationIds, form.get('location_id').value])

  const { selectableEmployeeIds, loading: loadingEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt
  )
  const { onSelectEmployee } = useOnSelectEmployees({
    form,
    selectedEmployees,
    setSelectedEmployees,
  })
  const { filters, logicOperator } = useEmployeeMultiFilters(
    getFilterValue,
    LOCAL_STORAGE_KEYS.shiftEmployees
  )

  useUnsafeEffect(() => {
    const setEmployees = async () => {
      const value = await applyFilters(
        employees.filter((e) => selectableEmployeeIds.includes(e.id)),
        filters,
        logicOperator,
        getFilterValue,
        () => undefined,
        employee,
        allMyDescendants
      )
      // Temporary fix: apply location filtering here since the new location filter doesn't automatically filter employees like the old multifilters system did
      const filteredEmployees = value?.filter(
        (em) => Number(em.attributes.location_id) === currentLocationId
      )
      setSelectedEmployees(() => filteredEmployees.map((user) => user.id))
    }
    setEmployees()
    if (!form.get('location_id').value) form.setValues({ location_id: currentLocationId })
  }, [selectableEmployeeIds.length, currentLocationId])

  useUnsafeEffect(() => {
    form.setValues({ employee_id: selectedEmployees })
  }, [selectedEmployees])

  const [createAutoSchedule, { loading }] = useMutation(GenerateAutoScheduleDocument, {
    extractorFn: (data) => data.shiftManagementMutations.generateScheduleShiftmanagementAutomation,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    track(trackingEvents.shiftManagement.autoSchedulerRun, {
      from: 'coverage_section',
      location_id: Number(form.get('location_id').value),
      agreement_hours: form.get('agreement_hours').value,
      max_work_hours_per_day: form.get('max_work_hours_per_day').value,
      rest_days_per_week: form.get('rest_days_per_week').value,
      rest_hours_between_shifts: form.get('rest_hours_between_shifts').value,
      start_date: form.get('start_at').value,
      end_date: form.get('end_at').value,
      employees_count: form.get('employee_id').value.length,
    })

    const res = await createAutoSchedule({
      variables: {
        agreementHours: form.get('agreement_hours').value,
        employeesIds: form.get('employee_id').value,
        endAt: form.get('end_at').value,
        locationId: Number(form.get('location_id').value),
        maxWorkHoursPerDay: form.get('max_work_hours_per_day').value,
        restDaysPerWeek: form.get('rest_days_per_week').value,
        restHoursBetweenShifts: form.get('rest_hours_between_shifts').value,
        startAt: form.get('start_at').value,
      },
    })
    apolloClient.refetchQueries({
      include: [ShiftManagementAutoScheduleStatusDocument],
    })
    const handled = handleResult(res, (_errors) => {})
    if (handled.success) {
      setIsSubscribed(true)
      closePortal()
    }
  }

  return (
    <Modal
      illustration="window-star"
      title={i18n.t('shift_management.coverage_section.automation.modal.title')}
      description={i18n.t('shift_management.coverage_section.automation.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Footer
          onClose={onClose}
          disabled={loading || selectedEmployees.length === 0 || !currentLocationId}
        />
      }
      disableFocusTrap
    >
      <Box width="full" flexDirection="column" gap="s16">
        <F0Select
          options={locations.map((location) => ({
            label: location.name,
            value: location.id.toString(),
          }))}
          value={currentLocationId.toString()}
          onChange={(value: string) => {
            form.setValues({ location_id: Number(value) })
          }}
          size="md"
          loading={locationsLoading}
          icon={Pin}
          label={i18n.t('shift_management.coverage_section.generate_coverage_modal.workplace')}
          showSearchBox
        />

        <Rules form={form} />

        <Box background="viridian100" padding="s24" borderRadius={{ all: 'abs012' }}>
          <Box flexDirection="row" gap="s4">
            <F0Icon icon={Lightbulb} size="md" />
            <Text.Header3>
              {i18n.t('shift_management.coverage_section.automation.modal.info.title')}
            </Text.Header3>
          </Box>
          <Text.Normal>
            {i18n.t('shift_management.coverage_section.automation.modal.info.description')}
            <Link.Href href="" target="_blank">
              {i18n.t('common.learn_more')}
            </Link.Href>
          </Text.Normal>
        </Box>

        {loadingEmployees ? (
          <Loading />
        ) : (
          <EmployeesSelector
            field={form.get('employee_id')}
            label={i18n.t('common.employees')}
            selectableEmployeeIds={selectableEmployeeIds}
            onSelectEmployee={onSelectEmployee}
            selectedEmployeeIds={selectedEmployees}
            width="s400"
            defaultGroup={Groups.WORKPLACES}
            filterProps={{
              defaultOption: {
                label: form.get('location_id').value.toString(),
                value: form.get('location_id').value.toString(),
              },
            }}
          />
        )}
      </Box>
    </Modal>
  )
})

export { NewAutoScheduleModal }
